import React, { useState, useEffect } from 'react';
import { UserPlus, UserCheck, Loader2 } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { BACKEND_URL } from '../config/api';

const FollowButton = ({ targetUserId, size = 'default' }) => {
  const { user } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!user || user.user_id === targetUserId) {
      setLoading(false);
      return;
    }
    const check = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/social/is-following/${targetUserId}`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setIsFollowing(data.is_following);
        }
      } catch {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [user, targetUserId]);

  const handleToggle = async () => {
    if (acting) return;
    setActing(true);
    const endpoint = isFollowing ? 'unfollow' : 'follow';
    try {
      const res = await fetch(`${BACKEND_URL}/api/social/${endpoint}/${targetUserId}`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setIsFollowing(data.is_following);
      }
    } catch {
      // Silent fail
    } finally {
      setActing(false);
    }
  };

  // Don't show for own profile
  if (!user || user.user_id === targetUserId) return null;
  if (loading) return null;

  const isSmall = size === 'small';

  return (
    <button
      onClick={handleToggle}
      disabled={acting}
      className={`flex items-center gap-1.5 rounded-lg font-medium transition-all ${
        isFollowing
          ? 'border border-sage text-sage hover:border-red-300 hover:text-red-500 hover:bg-red-50'
          : 'bg-sage text-white hover:bg-sage-dark'
      } ${isSmall ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm'}`}
      data-testid={`follow-btn-${targetUserId}`}
    >
      {acting ? (
        <Loader2 size={isSmall ? 12 : 14} className="animate-spin" />
      ) : isFollowing ? (
        <UserCheck size={isSmall ? 12 : 14} />
      ) : (
        <UserPlus size={isSmall ? 12 : 14} />
      )}
      {isFollowing ? 'Following' : 'Follow'}
    </button>
  );
};

export default FollowButton;
